import React, { FunctionComponent, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import FileList, { FileListFile } from '@murphy-frontend/web-core/components/FileList';
import Spinner from '@murphy-frontend/web-core/components/Spinner';
import SearchBar from '@murphy-frontend/web-core/components/SearchBar';
import Button from '@murphy-frontend/web-core/components/Button';
import EditFile from '@murphy-frontend/web-core/components/EditFile';
import FileUploadControl from '@murphy-frontend/web-core/components/FileUploadControl';
import { FileTypes, Roles } from '@murphy-frontend/common/enums';
import { useEditCustomerFile, useRemoveCustomerFile } from '../../Files/mutations';

import { FilesApi } from '@murphy-frontend/common/api/Files/FilesApi';
import { useGetCustomerFiles } from '../../Files/queries';
import { useGetCustomerFiles as useGetCustomerFilesV2 } from "@murphy-frontend/common/api/CustomerFiles/queries";
import queryKeys from '../../Files/queryKeys';
import { ModalConfiguration } from '@murphy-frontend/web-core/components/Modal';
import { useCustomer } from '../../../common/contexts/CustomerContext';
import { useUser } from "../../../common/contexts/UserContext";
import { TimeZoneType, getConvertedDate } from "@murphy-frontend/common/services/TimeService"
import { CustomerFileApi } from '@murphy-frontend/common/api/CustomerFiles/CustomerFilesApi';
import ApiServiceType, { IApiService } from "@murphy-frontend/common/interfaces/IApiService";
import { useInjection } from '@murphy-frontend/common/contexts/InversifyContext';
import { downloadFromLink } from '@murphy-frontend/common/utils';
import { CustomerFileResponse } from '@murphy-frontend/common/api/CustomerFiles/models';
import { toast } from 'react-toastify';
import { IconContext } from 'react-icons';
import { CiFolderOn } from 'react-icons/ci';
import { FaFolder } from 'react-icons/fa';
import { AiOutlinePlus } from 'react-icons/ai';
import { RiDeleteBin7Fill, RiPencilFill } from 'react-icons/ri';
import TableCellActionDropDownButton from '@murphy-frontend/web-core/components/ColumnGroupTable/TableCellActionDropDownButton';
import { useModal } from '@murphy-frontend/web-core/contexts/ModalContext';
import { useUserPreferences } from '../../../common/contexts/UserPreferencesContext';
import ComponentWrapper from '../../../common/components/ComponentWrapper';

const CrisisFiles: FunctionComponent = () => {
  const { customer } = useCustomer();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();
  const spawnModal = openModal;
  const { translations, timeZone } = useUserPreferences();

  const [error, setError] = useState(null);

  const [files, setFiles] = useState<Array<FileListFile>>([]);
  const [folders, setFolders] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState<Array<FileListFile>>([]);
  const [filteredFolders, setFilteredFolders] = useState<Array<FileListFile>>([]);
  const [currentSearchText, setCurrentSearchText] = useState("");
  const [currentFolder, setCurrentFolder] = useState<FileListFile>(null);
  const [allFiles, setAllFiles] = useState([]);
  const [allFolders, setAllFolders] = useState([])
  const [visibleDropDownId, setVisibleDropDownId] = useState(null)
  const { mutate: deleteFile, isPending: removeFileIsLoading } = useRemoveCustomerFile(
    customer.Id,
    translations
  );

  const {
    isLoading: isCustomerFilesLoading,
    isError: isErrorCustomerFiles,
    data: dataCustomerFiles,
    refetch: refetchFiles,
    error: getCustomerFilesError,
  } = useGetCustomerFiles(customer.Id);

  const { data: customerFilesV2, isLoading: isLoadingCustomerFilesV2 } = useGetCustomerFilesV2(customer.Id);
  const { mutate: editFile, isPending: editFileIsLoading } = useEditCustomerFile(customer.Id, translations);
  const apiService = useInjection<IApiService>(ApiServiceType.IApiService);
  const customerFilesApi = new CustomerFileApi(apiService);
  const filesAPI = new FilesApi(apiService);

  const traverseFiles = (folder, tz: TimeZoneType) => {
    const mappedFiles = folder.map((file: any) => ({
      id: file.Id,
      filepath: file.FilePath,
      filename: file.FileName,
      createddatetime: getConvertedDate(dayjs.utc(file.CreatedDateTime), tz),
      filetypename: "",
      addedBy: file.addedBy,
      isEditable: true,
      isDirectory: file.isDirectory,
      directoryName: file.directoryName,
      FileList: file.FileList?.length > 0 ? traverseFiles(file.FileList, tz) : [],
      folderId: file.folderId
    } as FileListFile));
    return mappedFiles;
  }
  const mapV1CustomerFiles = (filesArray, tz: TimeZoneType): Array<FileListFile> => {
    const mappedFiles = traverseFiles(filesArray, tz);
    return mappedFiles;
  }

  const mapV2CustomerFiles = (filesArray: CustomerFileResponse[], tz: TimeZoneType): Array<FileListFile> => {
    const mappedFiles = filesArray.map((file: CustomerFileResponse) => ({
      id: file.ID,
      filename: file.FileName,
      filetypename: file.FileTypeName,
      createddatetime: getConvertedDate(dayjs.utc(file.PeriodStart), tz),
      isEditable: false,
      onClickDownloadOverride: () => { handleDownloadV2(file) },
    } as FileListFile));
    return mappedFiles;
  }

  const handleDownloadV2 = async (customerFile: CustomerFileResponse) => {
    var file = await customerFilesApi.download(customer.Id, customerFile.ID);
    downloadFromLink(customerFile.FileName, file);
  }

  const onSubmitNewFile = (file: File) => {
    apiService.addFileToCustomer(file, customer.Id, currentFolder?.folderId).then((response) => {
      toast.info("File uploaded successfully");
      refetchFiles();
    }).catch((err) => {
      toast.error("Error uploading file");
    }).finally(() => {
      closeModal()
    });
  }

  function DeleteConfirmationBody({ name, fileCount, isFolder }: { name: string, fileCount?: number, isFolder?: boolean }) {
    return (
      <div>
        <p>
          {translations[isFolder ? "files-folders-delete-folder" : "del-file"]} <strong>{name}</strong>
        </p>
        {fileCount !== undefined && fileCount > 0 && (
          <p>
            ({translations["files-folders-this-will-delete"]} {fileCount} {fileCount === 1 ? translations["files-folders-file"] : translations["files-folders-files"]})
          </p>
        )}
      </div>
    );
  }

  useEffect(() => {
    if (isCustomerFilesLoading === false && isLoadingCustomerFilesV2 === false && timeZone) {
      let v1CustomerFiles = mapV1CustomerFiles(dataCustomerFiles, timeZone);
      const sharedCustomerFiles = customerFilesV2.filter((p) => p.IsShared === true);
      let v2NewCustomerFiles = mapV2CustomerFiles(sharedCustomerFiles, timeZone);
      const mergedFiles = [...v1CustomerFiles, ...v2NewCustomerFiles];
      let allFolders = mergedFiles.filter(file => file.isDirectory);
      let allFiles = mergedFiles.filter(file => !file.isDirectory);
      
      // Only reset current folder if we don't have one selected
      if (!currentFolder) {
        setFolders(allFolders);
        setFiles(allFiles);
      } else {
        // If we have a current folder, find it in the updated data and update its contents
        const updatedCurrentFolder = allFolders.find(f => f.folderId === currentFolder.folderId);
        
        if (updatedCurrentFolder) {
          setFolders(updatedCurrentFolder.FileList.filter(file => file.isDirectory));
          setFiles(updatedCurrentFolder.FileList.filter(file => !file.isDirectory));
        }
      }
      
      setAllFiles(allFiles);
      setAllFolders(allFolders);
    }
  }, [dataCustomerFiles, customerFilesV2, isCustomerFilesLoading, isLoadingCustomerFilesV2, timeZone]);

  const submitRemoveFile = (fileId) => {
    deleteFile(fileId);
  };

  const onClickNewFile = () => {
    const modalConf: ModalConfiguration = {
      title: translations["lang-button-add-file"],
      body: (
        <div>
          <FileUploadControl
            translations={translations}
            onClose={() => closeModal()}
            onSubmit={onSubmitNewFile}
          />
        </div>
      ),
      buttonText: "Ok",
      type: "info",
      hideButton: true,
      position: "center"
    };
    spawnModal(modalConf);
  };

  const onClickEditFile = (fileRow) => {
    const modalConf: ModalConfiguration = {
      title: translations.edit,
      body: (
        <EditFile
          id={fileRow.id}
          nameInit={fileRow.filename}
          onSuccess={() => closeModal()}
          onFail={() => closeModal()}
          onCancel={() => closeModal()}
          editFile
          translations={translations}
          onSaveEdit={editFile}
        />
      ),
      hideButton: true,
      type: "info",
      position: 'center'
    };

    spawnModal(modalConf);
  };

  const onRenameFolder = (folder: FileListFile) => {
    const modalConf: ModalConfiguration = {
      title: translations.edit,
      body: (
        <EditFile
          id={folder.folderId}
          nameInit={folder.directoryName}
          onSuccess={() => closeModal()}
          onFail={() => closeModal()}
          onCancel={() => closeModal()}
          translations={translations}
          onSaveEdit={editFolder}
        />
      ),
      hideButton: true,
      type: "info",
      position: 'center'
    };

    spawnModal(modalConf);
  };

  const onCreateNewFolder = () => {
    const modalConf: ModalConfiguration = {
      title: translations.edit,
      body: (
        <EditFile
          id={customer?.Id}
          onSuccess={() => closeModal()}
          createFolder
          onFail={() => closeModal()}
          onCancel={() => closeModal()}
          translations={translations}
          onSaveEdit={createNewFolder}
        />
      ),
      hideButton: true,
      type: "info",
      position: 'center'
    };

    spawnModal(modalConf);
  };

  const onClickRemoveFile = (fileRow) => {
    const modalConf: ModalConfiguration = {
      title: translations["delete"],
      body: <DeleteConfirmationBody name={fileRow.filename} />,
      hideButton: false,
      position: 'center',
      buttonText: translations["class-lang-user-table-delete"],
      type: "warning",
      okCallback: () => submitRemoveFile(fileRow.id),
    };

    spawnModal(modalConf);
  };

  const onClickRemoveFolder = (folder: FileListFile) => {
    const modalConf: ModalConfiguration = {
      title: translations["delete"],
      body: <DeleteConfirmationBody name={folder.directoryName} fileCount={folder.FileList.length} isFolder={true} />,
      hideButton: false,
      position: 'center',
      buttonText: translations["class-lang-user-table-delete"],
      type: "warning",
      okCallback: async () => {
        await filesAPI.deleteFolder(folder.folderId);
        refetchFiles()
      },
    };

    spawnModal(modalConf);
  }

  const onSearchChanged = (event) => {
    const newValue = event.target.value;
    setCurrentSearchText(newValue);
  };

  function ErrorMessage() {
    return <p>{error}</p>;
  }

  const onSearch = () => {
    if (files) {
      // let newFilteredFiles = mapOldCustomerFiles(files);
      let newFilteredFiles = [...files];
      let newFilteredFolders = [...folders];
      if (currentSearchText) {
        const searchValue = currentSearchText.toUpperCase();
        newFilteredFiles = newFilteredFiles.filter((p) =>
          p.filename.toUpperCase().startsWith(searchValue)
        );
        if (newFilteredFolders?.length > 0) {
          newFilteredFolders = newFilteredFolders.filter((p) =>
            p.directoryName.toUpperCase().startsWith(searchValue)
          );
        }
      }

      setFilteredFiles(newFilteredFiles);
      setFilteredFolders(newFilteredFolders);
    }
  }
  useEffect(() => {
    onSearch();
  }, [files]);

  useEffect(() => {
    if (error) {
      const modalConf: ModalConfiguration = {
        title: "Error",
        body: <ErrorMessage />,
        buttonText: "Ok",
        type: "error",
        okCallback: () => {
          setError(null);
        },
      };
      spawnModal(modalConf);
    }
  }, [error]);

  if (isCustomerFilesLoading || isLoadingCustomerFilesV2 || removeFileIsLoading || editFileIsLoading) {
    return (
      <div className="spinner-container-global-center">
        <Spinner />
      </div>
    );
  }

  const onOpenFolder = async (folder: FileListFile) => {
    setCurrentFolder(folder);
    setFolders(folder.FileList.filter(file => file.isDirectory));
    setFiles(folder.FileList.filter(file => !file.isDirectory));
  }

  const onResetFolder = () => {
    setCurrentFolder(null);
    setFolders(allFolders);
    setFiles(allFiles);
  }

  const createNewFolder = async (folderToUpload) => {
    await filesAPI.createFolder(folderToUpload);
    closeModal();
    refetchFiles();
  }

  const editFolder = async (request) => {
    await filesAPI.updateFolderName(request);
    closeModal();
    refetchFiles();
  }

  const generateDropDownListItems = (folder) => [
    (
      <li key="edit" onClick={() => onRenameFolder(folder)}>
        <div className="vertical-aligner">
          <IconContext.Provider value={{ className: 'dropdown-icon' }}>
            <RiPencilFill />
          </IconContext.Provider>
          <span>{translations ? translations.rename : "Rename"}</span>
        </div>
      </li>
    ),
    (
      <li key="delete" onClick={() => onClickRemoveFolder(folder)}>
        <div className="vertical-aligner">
          <IconContext.Provider value={{ className: 'dropdown-icon' }}>
            <RiDeleteBin7Fill />
          </IconContext.Provider>
          <span>{translations ? translations.delete : "Delete"}</span>
        </div>
      </li>
    ),
  ];

  return (
    <ComponentWrapper active={7}>
      <div>
        {user?.Role !== Roles.Trial && (
          <div className="one" style={{ display: 'flex', paddingLeft: 20, paddingRight: 20, width: '80rem', flexDirection: 'row', alignItems: 'center' }}>
            <span style={{ fontSize: 22, fontWeight: 'bold' }}>{translations ? translations.appfstitle : "Files"}</span>
            <div style={{ width: '80rem', flexDirection: 'row', display: 'flex', justifyContent: 'flex-end' }}>
              {user?.Role === Roles.Admin && !currentFolder && <div onClick={onCreateNewFolder} style={{ cursor: 'pointer', height: 40, width: '14rem', display: 'flex', borderWidth: 1, flexDirection: 'row', backgroundColor: '#FFFFFF', borderColor: '#00B2A9', borderStyle: 'solid', alignItems: 'center', paddingLeft: 10, paddingRight: 10, paddingBottom: 10, paddingTop: 10, borderRadius: 5 }}>
                <IconContext.Provider value={{ className: 'btn-icon' }}>
                  <AiOutlinePlus color="#00B2A9" />
                </IconContext.Provider>
                <span style={{ marginLeft: 10, color: "#00B2A9" }}>{translations ? translations.createFolder : "Create new folder"}</span>
              </div>}
              <div style={{ marginLeft: 20 }}>
                <Button
                  isIconButton
                  showIconLeft
                  buttonStyle="btn--upload"
                  buttonSize="btn-with-icon"
                  onClick={onClickNewFile}
                >
                  <span className="btn-with-icon-text">
                    {translations.uploadnewfile}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
      <div className="three" style={{ display: 'flex', paddingLeft: 20, paddingRight: 20, width: '80rem', marginTop: 20 }}>
        <div style={{ width: '80rem' }}>
          <SearchBar
            val={currentSearchText}
            handleChange={onSearchChanged}
            placeholder={translations["placeholder-lang-search-action"]}
            showSearchIcon={true}
          />
        </div>
        <div style={{ marginLeft: 20 }}>
          <Button
            isDarkButton
            onClick={onSearch}
          >
            <span className="btn-with-icon-text">
              {translations ? translations.search : "Search"}
            </span>
          </Button>
        </div>
      </div>
      {currentFolder && <div style={{ marginTop: 30, paddingLeft: 20 }}>
        <label style={{ textDecorationLine: 'underline', marginRight: 10, cursor: 'pointer' }} onClick={() => onResetFolder()}>{translations ? translations.fileLib : "File Library"} {'>'}</label>
        <label style={{ fontWeight: 'bold' }}>{currentFolder?.directoryName}</label>
      </div>}
      {folders?.length > 0 && !currentFolder && <div style={{ marginTop: 30, width: '80rem', paddingLeft: 10 }}>
        <span style={{ marginLeft: 10 }}>{translations ? translations.folders : "Folders"}</span>
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: 10, whiteSpace: 'nowrap' }}>
          {filteredFolders?.map(folder => <div onClick={() => onOpenFolder(folder)} style={{ cursor: 'pointer', height: '10%', width: '15rem', display: 'flex', marginBottom: 10, marginLeft: 10, borderWidth: 1, flexDirection: 'row', backgroundColor: '#FFFFFF', borderColor: '#EBEBEB', borderStyle: 'solid', alignItems: 'center', paddingLeft: 10, paddingRight: 10, paddingBottom: 10, paddingTop: 10, borderRadius: 5 }}>
            <div style={{ marginRight: 10 }}>
              <IconContext.Provider value={{ className: 'btn-icon' }}>
                {folder?.FileList?.length > 0 ? <FaFolder color="grey" /> : <CiFolderOn />}
              </IconContext.Provider>
            </div>
            <TableCellActionDropDownButton
              onClickRow={() => { }}
              displayText={folder.directoryName}
              listItems={generateDropDownListItems(folder)}
              columnSize="column-large"
              showDropDown = {(id) => setVisibleDropDownId(id)}
						  dropDownId = {visibleDropDownId}
						  rowId = {folder.folderId}
            />
          </div>)}
        </div>
      </div>}
      {files?.length > 0 && <div style={{ width: '90rem', display: 'flex', flexDirection: 'column', paddingLeft: 20, marginTop: 10 }}>
        <span>{translations ? translations.appfstitle : "Files"}</span>
        <div style={{ width: '90rem' }}>
          <FileList
            translations={translations}
            files={filteredFiles}
            onClickRemoveFile={onClickRemoveFile}
            onClickEditFile={onClickEditFile}
            style={{ width: '75rem' }}
          />
        </div>
      </div>}
    </ComponentWrapper>
  );
};

export default CrisisFiles;
